import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addBranch,
  deleteBranch,
  getBranches,
  updateBranch,
} from "../../../../store/actions/branchActions";

const BranchManagement = ({ provider }) => {
  const dispatch = useDispatch();
  const branchState = useSelector((state) => state.branches || {});
  const branchesFromStore = branchState.list || [];

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);

  const [branchForm, setBranchForm] = useState({
    branchName: "",
    address: "",
    city: "",
    latitude: "",
    longitude: "",
    isActive: true,
  });

  const branchesPerPage = 5;

  useEffect(() => {
    if (provider?._id) {
      setLoading(true);
      dispatch(getBranches(provider._id));
    }
  }, [provider, dispatch]);

  useEffect(() => {
    setLoading(false);
  }, [branchesFromStore]);

  useEffect(() => {
    setPage(1);
  }, [search]);



  const filteredBranches = branchesFromStore.filter((b) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (b.branchName || "").toLowerCase().includes(q) ||
      (b.address || "").toLowerCase().includes(q) ||
      (b.phone || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filteredBranches.length / branchesPerPage) || 1;
  const currentBranches = filteredBranches.slice(
    (page - 1) * branchesPerPage,
    page * branchesPerPage
  );

  const openAddDialog = () => {
    setEditingBranch(null);
    setBranchForm({ branchName: "", address: "", phone: "" });
    setOpenDialog(true);
  };

  const openEditDialog = (branch) => {
    setEditingBranch(branch);
    setBranchForm({
      branchName: branch.name,
      address: branch.address,
      city: branch.city,
      latitude: branch.latitude,
      longitude: branch.longitude,
      isActive: branch.isActive
    });
    setOpenDialog(true);
  };

  const handleSaveBranch = () => {
    const payload = {
      name: branchForm.branchName,
      address: branchForm.address,
      city: branchForm.city,
      providerId: provider?._id,
      latitude: branchForm.latitude,
      longitude: branchForm.longitude,
      isActive: branchForm.isActive ?? true,
    };

    if (editingBranch?.branchId) {

      dispatch(updateBranch({ payload:{...payload, branchId: editingBranch.branchId}}));
    } else {
      dispatch(addBranch(payload));
    }

    dispatch(getBranches(provider._id));
    setOpenDialog(false);
    setEditingBranch(null);
    setBranchForm({ branchName: "", address: "", city: "", latitude: 0.0, longitude: 0.0, isActive: true });
  };

  const handleDelete = (branch) => {
    if (!window.confirm(`Are you sure you want to delete this branch? ${JSON.stringify(branch.branchId)}`)) return;
    dispatch(deleteBranch({ branchId: branch.branchId, providerId: provider._id }));
    // dispatch(getBranches(provider._id));
  };

  return (
    <Box sx={{ maxWidth: "99%", mx: "auto" }}>
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search branches..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
          }}
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
        >
          Add Branch
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : currentBranches.length === 0 ? (
        <Typography>No branches found</Typography>
      ) : (
        <Stack spacing={2}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Provider Logo</TableCell>
                  <TableCell>Provider Name</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Latitude</TableCell>
                  <TableCell>Longitude</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentBranches.map((b) => (
                  <TableRow key={b.branchId}>
                    {/* Provider Name + Logo */}
                    <TableCell>
                      <Avatar
                        src={b.providerLogo}
                        alt={b.providerName}
                        sx={{ width: 32, height: 32, mr: 1 }}
                      />

                    </TableCell>

                    <TableCell>{b.providerName}</TableCell>
                    <TableCell>{b.name}</TableCell>
                    <TableCell>{b.city}</TableCell>
                    <TableCell>{b.address}</TableCell>
                    <TableCell>{b.latitude}</TableCell>
                    <TableCell>{b.longitude}</TableCell>
                    <TableCell>
                      {b.isActive ? "✅ Active" : "❌ Inactive"}
                    </TableCell>
                    <TableCell>
                      {new Date(b.createdAt).toLocaleDateString()}
                    </TableCell>

                    {/* Edit + Delete */}
                    <TableCell align="right">
                      <IconButton
                        onClick={() => openEditDialog(b)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(b)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, v) => setPage(v)}
          />
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} PaperProps={{
        sx: {
          width: "50%",        // Half of the screen
          maxWidth: "none",    // Disable default maxWidth limit
          margin: "auto",      // Center it
        },
      }}>
        <DialogTitle>
          {editingBranch ? "Edit Branch" : "Add Branch"}
        </DialogTitle>
        <DialogContent
          sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          <TextField
            label="Branch Name"
            value={branchForm.branchName}
            onChange={(e) =>
              setBranchForm({ ...branchForm, branchName: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Address"
            value={branchForm.address}
            onChange={(e) =>
              setBranchForm({ ...branchForm, address: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="City"
            value={branchForm.city}
            onChange={(e) =>
              setBranchForm({ ...branchForm, city: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Latitude"
            value={branchForm.latitude}
            onChange={(e) =>
              setBranchForm({ ...branchForm, latitude: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Longitude"
            value={branchForm.longitude}
            onChange={(e) =>
              setBranchForm({ ...branchForm, longitude: e.target.value })
            }
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={branchForm.isActive}
                value={branchForm.isActive}
                onChange={(e) =>
                  setBranchForm({ ...branchForm, isActive: e.target.checked })
                }
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveBranch}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchManagement;
